import React, { useState, useEffect, useCallback } from "react";
import api from '../../lib/axios';
import { toast } from "sonner";
import {
  AlertTriangle,
  Bell,
  BellOff,
  DollarSign,
  Settings,
  TrendingUp,
  CheckCircle2,
  RefreshCw,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);
};

/**
 * PriceAlertsManager - Component for configuring and viewing price alerts
 */
const PriceAlertsManager = ({ isOpen, onClose }) => {
  const [config, setConfig] = useState({
    enabled: false,
    threshold_cost_per_m2: 500,
    notification_type: "toast",
    check_on_upload: true,
    check_on_save: true
  });
  const [highCostItems, setHighCostItems] = useState({
    quote_types: [],
    factory_costs: [],
    total_alerts: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await api.get(`${API}/alerts/price/config`);
      setConfig(response.data);
    } catch (error) {
      console.error("Error loading price alerts config:", error);
    }
  }, []);

  const fetchHighCostItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`${API}/alerts/price/high-cost-items`);
      setHighCostItems(response.data);
    } catch (error) {
      console.error("Error loading high cost items:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchConfig();
      fetchHighCostItems();
    }
  }, [isOpen, fetchConfig, fetchHighCostItems]);

  const handleSaveConfig = async () => {
    setIsSaving(true);
    try {
      await api.put(`${API}/alerts/price/config`, config);
      toast.success("Configuración de alertas guardada");
      fetchHighCostItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar configuración");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Alertas de Precio
          </DialogTitle>
          <DialogDescription>
            Configura alertas para precios que excedan un umbral
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1">
          <div className="space-y-6 pr-4">
            {/* Configuration Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Configuración
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Enable Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {config.enabled ? (
                      <Bell className="w-6 h-6 text-amber-500" />
                    ) : (
                      <BellOff className="w-6 h-6 text-slate-400" />
                    )}
                    <div>
                      <p className="font-medium">Alertas de Precio Alto</p>
                      <p className="text-sm text-slate-500">
                        {config.enabled ? "Activadas" : "Desactivadas"}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={config.enabled}
                    onCheckedChange={(checked) => 
                      setConfig({ ...config, enabled: checked })
                    }
                    data-testid="price-alerts-toggle"
                  />
                </div>

                {/* Threshold */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="threshold">Umbral ($/m²)</Label>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-slate-400" />
                      <Input
                        id="threshold"
                        type="number"
                        step="10"
                        min="0"
                        value={config.threshold_cost_per_m2}
                        onChange={(e) => 
                          setConfig({ ...config, threshold_cost_per_m2: parseFloat(e.target.value) || 0 })
                        }
                        disabled={!config.enabled}
                        className="flex-1"
                        data-testid="price-threshold-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Alertar cuando el costo/m² exceda este valor
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="notificationType">Tipo de Notificación</Label>
                    <Select
                      value={config.notification_type}
                      onValueChange={(value) => 
                        setConfig({ ...config, notification_type: value })
                      }
                      disabled={!config.enabled}
                    >
                      <SelectTrigger id="notificationType" data-testid="notification-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="toast">Toast (Temporal)</SelectItem>
                        <SelectItem value="badge">Badge (Permanente)</SelectItem>
                        <SelectItem value="both">Ambos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* When to Check */}
                <div className="space-y-2">
                  <Label>Verificar alertas:</Label>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.check_on_upload}
                        onChange={(e) => 
                          setConfig({ ...config, check_on_upload: e.target.checked })
                        }
                        disabled={!config.enabled}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm">Al cargar archivos</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.check_on_save}
                        onChange={(e) => 
                          setConfig({ ...config, check_on_save: e.target.checked })
                        }
                        disabled={!config.enabled}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm">Al guardar items</span>
                    </label>
                  </div>
                </div>

                {/* Save Button */}
                <Button
                  onClick={handleSaveConfig}
                  disabled={isSaving}
                  className="w-full"
                  data-testid="save-price-alerts-config"
                >
                  {isSaving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Guardar Configuración
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Current Alerts */}
            <Card className={highCostItems.total_alerts > 0 ? "border-amber-300 bg-amber-50/30" : ""}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-amber-600" />
                    Items con Precio Alto
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant={highCostItems.total_alerts > 0 ? "destructive" : "secondary"}>
                      {highCostItems.total_alerts} alertas
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchHighCostItems}
                      disabled={isLoading}
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
                <CardDescription>
                  Items que superan el umbral de {formatCurrency(config.threshold_cost_per_m2)}/m²
                </CardDescription>
              </CardHeader>
              <CardContent>
                {highCostItems.total_alerts === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
                    <p>No hay items que excedan el umbral</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Quote Types with high cost */}
                    {highCostItems.quote_types.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-slate-700 mb-2">
                          Índice de Precios ({highCostItems.quote_types.length})
                        </h4>
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50">
                                <TableHead>Nombre</TableHead>
                                <TableHead>Material</TableHead>
                                <TableHead>Proyecto</TableHead>
                                <TableHead className="text-right">Costo/m²</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {highCostItems.quote_types.map((item) => (
                                <TableRow key={item.id} className="hover:bg-amber-50">
                                  <TableCell className="font-medium">{item.name}</TableCell>
                                  <TableCell>
                                    <Badge variant="outline">{item.material}</Badge>
                                  </TableCell>
                                  <TableCell className="text-slate-500">
                                    {item.project_name || "—"}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-amber-600">
                                    {formatCurrency(item.average_cost_per_m2)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}

                    {/* Factory Costs with high cost */}
                    {highCostItems.factory_costs.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-slate-700 mb-2">
                          Costos de Fábrica ({highCostItems.factory_costs.length})
                        </h4>
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50">
                                <TableHead>Nombre</TableHead>
                                <TableHead>Fábrica</TableHead>
                                <TableHead>Proyecto</TableHead>
                                <TableHead className="text-right">Costo/m²</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {highCostItems.factory_costs.map((item) => (
                                <TableRow key={item.id} className="hover:bg-amber-50">
                                  <TableCell className="font-medium">{item.name}</TableCell>
                                  <TableCell>
                                    <Badge variant="outline">{item.factory_name || "—"}</Badge>
                                  </TableCell>
                                  <TableCell className="text-slate-500">
                                    {item.project_name || "—"}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-amber-600">
                                    {formatCurrency(item.average_cost_per_m2)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </ScrollArea>

        <DialogFooter className="mt-4 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * PriceAlertBadge - Small badge component for navbar
 */
export const PriceAlertBadge = ({ onClick }) => {
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    const fetchAlertCount = async () => {
      try {
        const response = await api.get(`${API}/alerts/price/high-cost-items`);
        if (response.data.enabled) {
          setAlertCount(response.data.total_alerts);
        }
      } catch (error) {
        console.error("Error fetching alert count:", error);
      }
    };
    
    fetchAlertCount();
    const interval = setInterval(fetchAlertCount, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  if (alertCount === 0) return null;

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className="relative"
      data-testid="price-alert-badge"
    >
      <AlertTriangle className="w-5 h-5 text-amber-500" />
      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
        {alertCount > 9 ? "9+" : alertCount}
      </span>
    </Button>
  );
};

export default PriceAlertsManager;
